import { Controller } from "@hotwired/stimulus"
import maplibregl from "maplibre-gl"

// Default zoom levels by geo type when a user picks a location.
const ZOOM_BY_GEO_TYPE = {
  msa: 7,
  city: 10,
  zip: 12
}

// Bright, high-energy palette — matches the server. Red = hot/bad, green = go,
// orange = caution, blue = cool/low.
const P_RATIO_COLORS = {
  hyper_competitive: "#FF1744",
  competitive:       "#FF9100",
  sweet_spot:        "#00E676",
  over_supply:       "#FF9100",
  hyper_over_supply: "#FF1744"
}

const P_RATIO_LABELS = {
  hyper_competitive: "Hyper Competitive",
  competitive:       "Competitive",
  sweet_spot:        "Sweet Spot",
  over_supply:       "Over-Supply",
  hyper_over_supply: "Hyper Over-Supply"
}

const DISTRESS_COLORS  = { low: "#00E676", moderate: "#FF9100", high: "#FF1744" }
// High investor activity = lots of cash buyers = GOOD for a wholesaler → green.
const INVESTOR_COLORS  = { low: "#FF1744", moderate: "#FFEA00", high: "#00E676" }
const LOW_MOD_HIGH     = { low: "Low", moderate: "Moderate", high: "High" }

// Which feature property holds the color for each metric selection.
const COLOR_FIELD_BY_METRIC = {
  p_ratio:            "p_ratio_color",
  distress_rate:      "distress_color",
  investor_activity:  "investor_color"
}

// Stage → color for the GridSignal data-center pin layer. Matches the
// server (Api::GridSignal::MapProjectsController::STAGE_COLORS) so the
// legend reads from the same palette whether the server emits it or
// the client renders it from this map.
const DC_STAGE_COLORS = {
  OPERATING:    "#00E676",
  CONSTRUCTION: "#2196F3",
  PERMITTING:   "#FFC107",
  ZONING:       "#FFC107",
  SITE_CONTROL: "#FF9100",
  UTILITY:      "#FF9100",
  INCENTIVE:    "#FF9100",
  STUDY:        "#9C27B0",
  DISCUSSION:   "#9C27B0",
  UNKNOWN:      "#94A3B8"
}

// Legend groups collapse the 10 stages into the 5 colored buckets a
// user actually scans visually (same color = same bucket). Order
// goes maturity-descending so the legend reads as a timeline.
// Each group is also a filter chip: tap to hide/show those pins.
const DC_LEGEND_GROUPS = [
  { key: "operating",    color: DC_STAGE_COLORS.OPERATING,    label: "Operating",                stages: ["OPERATING"] },
  { key: "construction", color: DC_STAGE_COLORS.CONSTRUCTION, label: "Construction",             stages: ["CONSTRUCTION"] },
  { key: "permitting",   color: DC_STAGE_COLORS.PERMITTING,   label: "Permitting / Zoning",      stages: ["PERMITTING", "ZONING"] },
  { key: "site_control", color: DC_STAGE_COLORS.SITE_CONTROL, label: "Site control / Incentive", stages: ["SITE_CONTROL", "UTILITY", "INCENTIVE"] },
  { key: "study",        color: DC_STAGE_COLORS.STUDY,        label: "Discussion / Study",       stages: ["STUDY", "DISCUSSION"] },
  { key: "unknown",      color: DC_STAGE_COLORS.UNKNOWN,      label: "Unknown",                  stages: ["UNKNOWN"] }
]
// "Early stage" = not built or under construction yet: where the next
// wave of growth shows up first.
const DC_EARLY_STAGE_GROUPS = ["permitting", "site_control", "study"]

function dcGroupFor(stage) {
  const s = (stage || "UNKNOWN").toString().toUpperCase()
  return (DC_LEGEND_GROUPS.find(g => g.stages.includes(s)) || DC_LEGEND_GROUPS[DC_LEGEND_GROUPS.length - 1]).key
}

// For the legend display.
const LEGEND_FOR_METRIC = {
  p_ratio: {
    title: "True Rank (p-Ratio)",
    tiers: [
      { key: "hyper_competitive", color: P_RATIO_COLORS.hyper_competitive, label: "Hyper Comp" },
      { key: "competitive",       color: P_RATIO_COLORS.competitive,       label: "Competitive" },
      { key: "sweet_spot",        color: P_RATIO_COLORS.sweet_spot,        label: "Sweet Spot" },
      { key: "over_supply",       color: P_RATIO_COLORS.over_supply,       label: "Over-Supply" },
      { key: "hyper_over_supply", color: P_RATIO_COLORS.hyper_over_supply, label: "Hyper Over" }
    ]
  },
  distress_rate: {
    title: "Distress Rate",
    tiers: [
      { key: "low",      color: DISTRESS_COLORS.low,      label: "Low" },
      { key: "moderate", color: DISTRESS_COLORS.moderate, label: "Moderate" },
      { key: "high",     color: DISTRESS_COLORS.high,     label: "High" }
    ]
  },
  investor_activity: {
    title: "Investor Activity",
    tiers: [
      { key: "low",      color: INVESTOR_COLORS.low,      label: "Low" },
      { key: "moderate", color: INVESTOR_COLORS.moderate, label: "Moderate" },
      { key: "high",     color: INVESTOR_COLORS.high,     label: "High" }
    ]
  }
}

export default class extends Controller {
  static targets = [
    "mapContainer", "searchInput", "searchResults",
    "viewType", "metric", "period",
    "legend", "infoPanel", "infoPanelBody", "loading",
    "parentGeo", "progressBar", "progressFill", "progressPct",
    "dcSlider", "dcSliderBody"
  ]

  static values = {
    geographyId: Number,
    geographyName: String,
    geographyType: String,
    geographyLat: Number,
    geographyLng: Number,
    tileStyleUrl: { type: String, default: "https://tiles.openfreemap.org/styles/positron" }
  }

  connect() {
    this.searchTimeout = null
    this.highlightedResultIndex = -1
    this._hideResultsOnOutsideClick = this._hideResultsOnOutsideClick.bind(this)
    document.addEventListener("click", this._hideResultsOnOutsideClick)
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    // State the layer paints key off of. Default from the <select> markup.
    this.currentMetric = this.hasMetricTarget ? this.metricTarget.value : "p_ratio"
    this.currentPeriod = this.hasPeriodTarget ? this.periodTarget.value : "90d"
    this.currentViewType = this.hasViewTypeTarget ? this.viewTypeTarget.value : "auto"
    this.autoMode = this.currentViewType === "auto"
    this.currentGeoType = this.autoMode
      ? "msa"
      : ({ msas: "msa", cities: "city", zips: "zip" }[this.currentViewType] || "msa")
    this.featuresById = new Map()

    this.initMap()
  }

  disconnect() {
    document.removeEventListener("click", this._hideResultsOnOutsideClick)
    if (this._dcEscapeHandler) {
      document.removeEventListener("keydown", this._dcEscapeHandler)
      this._dcEscapeHandler = null
    }
    if (this.map) {
      this.map.remove()
      this.map = null
    }
  }

  initMap() {
    const usBounds = [[-125.0, 24.0], [-66.5, 49.5]]

    this.map = new maplibregl.Map({
      container: this.mapContainerTarget,
      style: this.tileStyleUrlValue,
      bounds: usBounds,
      fitBoundsOptions: { padding: 40 },
      attributionControl: { compact: true }
    })

    this.map.addControl(new maplibregl.NavigationControl({ visualizePitch: false, showCompass: false }), "top-right")

    this.map.on("load", () => {
      this.map.resize()
      // Auto-mode starts from the initial zoom (national = msa). After the
      // first _loadMarkets we start listening for zoom crossings.
      this._resolveAutoLayer({ skipLoad: true })
      this._loadMarkets()
      if (this.hasGeographyIdValue && this.geographyIdValue > 0) {
        this._flyToPreselectedGeography()
      }
    })

    this.map.on("zoomend", () => this._onZoomEnd())

    this.map.scrollZoom.setZoomRate(1 / 200)
  }

  // ---------- Automatic layer selection by zoom ----------

  _onZoomEnd() {
    if (!this.autoMode) return
    // Data Centers mode is a point layer that spans the whole US at
    // every zoom — the MSA/city/zip auto-swap doesn't apply. Letting
    // _resolveAutoLayer fire _loadMarkets here would re-render the
    // TrueRank legend on top of the DC legend on every scroll.
    if (this.currentMetric === "data_centers") return
    this._resolveAutoLayer()
  }

  _resolveAutoLayer(opts = {}) {
    const zoom = this.map.getZoom()
    let next
    if (zoom < 6) next = "msa"
    else if (zoom < 10) next = "city"
    else next = "zip"

    if (next !== this.currentGeoType) {
      this.currentGeoType = next
      if (!opts.skipLoad) this._loadMarkets()
    }
  }

  _flyToPreselectedGeography() {
    const lat = this.geographyLatValue
    const lng = this.geographyLngValue
    const type = this.geographyTypeValue
    const name = this.geographyNameValue

    if (lat && lng && lat !== 0 && lng !== 0) {
      this.map.flyTo({
        center: [lng, lat],
        zoom: ZOOM_BY_GEO_TYPE[type] || 9,
        speed: 1.4,
        curve: 1.2,
        essential: true
      })
      if (name) {
        this.searchInputTarget.value = name
        this._renderSelectedChip(name)
      }
      // If an MSA layer is loaded and we have the feature, pop it once the
      // camera settles.
      if (type === "msa") {
        setTimeout(() => {
          const feature = this.featuresById.get(this.geographyIdValue)
          if (feature) this._openMarketPopup(feature)
        }, 700)
      }
    } else {
      this._showToast(`${name || "This geography"} has no coordinates saved. Click Backfill Map Coordinates on Admin → Data Imports.`)
    }
  }

  // ---------- Markets layer ----------

  async _loadMarkets() {
    // Guard: in Data Centers mode the market layer isn't visible, so
    // skip the whole fetch + legend render. Also prevents the TrueRank
    // legend from stomping the DC legend on auto zoom crossings.
    if (this.currentMetric === "data_centers") return
    try {
      const geoType = this.currentGeoType || "msa"
      const response = await fetch(`/api/map/markets?geo_type=${geoType}&metric=${this.currentMetric}&period=${this.currentPeriod}`, {
        headers: { "Accept": "application/json" },
        credentials: "same-origin"
      })
      if (!response.ok) {
        console.error("Markets fetch failed:", response.status)
        return
      }
      const geojson = await response.json()

      // Index features by geography id so search can resolve coords for a
      // selected result even when the geography record itself has no stored
      // lat/lng (common for cities whose centroids weren't backfilled).
      this.featuresById.clear()
      geojson.features.forEach(f => this.featuresById.set(f.properties.id, f))

      const colorField = COLOR_FIELD_BY_METRIC[this.currentMetric] || "p_ratio_color"

      if (this.map.getSource("markets")) {
        this.map.getSource("markets").setData(geojson)
      } else {
        this.map.addSource("markets", { type: "geojson", data: geojson })

        this.map.addLayer({
          id: "markets-halo",
          type: "circle",
          source: "markets",
          paint: {
            "circle-radius": [
              "interpolate", ["linear"], ["zoom"],
              3, ["+", 10, ["min", 14, ["/", ["coalesce", ["get", "population"], 100000], 300000]]],
              8, ["+", 22, ["min", 32, ["/", ["coalesce", ["get", "population"], 100000], 150000]]]
            ],
            "circle-color": ["get", colorField],
            "circle-opacity": 0.22,
            "circle-blur": 0.55
          }
        })

        this.map.addLayer({
          id: "markets-fill",
          type: "circle",
          source: "markets",
          paint: {
            "circle-radius": [
              "interpolate", ["linear"], ["zoom"],
              3, ["+", 6, ["min", 7, ["/", ["coalesce", ["get", "population"], 100000], 500000]]],
              8, ["+", 12, ["min", 16, ["/", ["coalesce", ["get", "population"], 100000], 300000]]]
            ],
            "circle-color": ["get", colorField],
            "circle-opacity": 0.95,
            "circle-stroke-color": "#ffffff",
            "circle-stroke-width": 2,
            "circle-stroke-opacity": 0.95
          }
        })

        this._wireMarketInteractions()
      }

      this._applyMetricPaint()
      this._renderLegend(geojson.features.length)
    } catch (error) {
      console.error("Markets layer error:", error)
    }
  }

  _applyMetricPaint() {
    const colorField = COLOR_FIELD_BY_METRIC[this.currentMetric] || "p_ratio_color"
    if (this.map.getLayer("markets-fill")) {
      this.map.setPaintProperty("markets-fill", "circle-color", ["get", colorField])
    }
    if (this.map.getLayer("markets-halo")) {
      this.map.setPaintProperty("markets-halo", "circle-color", ["get", colorField])
    }
  }

  _renderLegend(count) {
    if (!this.hasLegendTarget) return
    // Belt-and-suspenders: Data Centers uses a different legend
    // palette (stage colors), rendered by _renderDataCentersLegend.
    // If any code path reaches this method while the DC metric is
    // active, re-route rather than falling back to p_ratio.
    if (this.currentMetric === "data_centers") {
      this._renderDataCentersLegend(this._dcFeatureCount || 0)
      return
    }
    const spec = LEGEND_FOR_METRIC[this.currentMetric] || LEGEND_FOR_METRIC.p_ratio
    const items = spec.tiers.map(t =>
      `<span class="map-legend-tier" style="background:${t.color}" title="${t.label}">${t.label}</span>`
    ).join("")
    this.legendTarget.innerHTML = `
      <div class="map-legend-label">${spec.title} · ${count} markets</div>
      <div class="map-legend-tiers">${items}</div>
    `
    this.legendTarget.style.display = ""
  }

  _wireMarketInteractions() {
    this.map.on("mouseenter", "markets-fill", () => { this.map.getCanvas().style.cursor = "pointer" })
    this.map.on("mouseleave", "markets-fill", () => { this.map.getCanvas().style.cursor = "" })
    this.map.on("click", "markets-fill", (e) => {
      const feature = e.features?.[0]
      if (!feature) return
      this._openMarketPopup(feature)
    })
  }

  _openMarketPopup(feature) {
    const p = feature.properties
    const [lng, lat] = feature.geometry.coordinates
    const fmt = (n) => (n != null && !isNaN(n)) ? Number(n).toLocaleString() : "—"
    const money = (n) => (n != null && !isNaN(n)) ? "$" + Math.round(n).toLocaleString() : "—"

    const activeLabel = this._activeMetricLabelFor(p)
    const activeColor = this._activeMetricColorFor(p)

    const html = `
      <div class="market-popup">
        <div class="market-popup-head">
          <div class="market-popup-tier" style="background:${activeColor}">${activeLabel}</div>
          <div class="market-popup-name">${this._escape(p.name)}</div>
        </div>
        <div class="market-popup-stats">
          <div class="market-popup-stat">
            <span class="market-popup-stat-label">p-Ratio</span>
            <span class="market-popup-stat-value">${p.p_ratio ?? "—"}</span>
          </div>
          <div class="market-popup-stat">
            <span class="market-popup-stat-label">Distress rate</span>
            <span class="market-popup-stat-value">${p.distress_rate != null ? p.distress_rate + "%" : "—"}</span>
          </div>
          <div class="market-popup-stat">
            <span class="market-popup-stat-label">Investor share</span>
            <span class="market-popup-stat-value">${p.investor_share != null ? p.investor_share + "%" : "—"}</span>
          </div>
          <div class="market-popup-stat">
            <span class="market-popup-stat-label">Median price</span>
            <span class="market-popup-stat-value">${money(p.median_price)}</span>
          </div>
          <div class="market-popup-stat">
            <span class="market-popup-stat-label">Active listings</span>
            <span class="market-popup-stat-value">${fmt(p.active)}</span>
          </div>
          <div class="market-popup-stat">
            <span class="market-popup-stat-label">Population</span>
            <span class="market-popup-stat-value">${fmt(p.population)}</span>
          </div>
        </div>
      </div>
    `

    new maplibregl.Popup({ closeOnClick: true, maxWidth: "320px", className: "market-popup-wrapper" })
      .setLngLat([lng, lat])
      .setHTML(html)
      .addTo(this.map)
  }

  _activeMetricLabelFor(props) {
    switch (this.currentMetric) {
      case "distress_rate":     return props.distress_tier_label ? `${LOW_MOD_HIGH[props.distress_tier]} Distress` : "No distress data"
      case "investor_activity": return props.investor_tier_label ? `${LOW_MOD_HIGH[props.investor_tier]} Investor` : "No investor data"
      default:                  return props.p_ratio_tier_label || "Insufficient data"
    }
  }

  _activeMetricColorFor(props) {
    switch (this.currentMetric) {
      case "distress_rate":     return props.distress_color
      case "investor_activity": return props.investor_color
      default:                  return props.p_ratio_color
    }
  }

  // ---------- Control changes (now real) ----------

  onViewTypeChange(event) {
    this.currentViewType = event.target.value
    this.autoMode = this.currentViewType === "auto"

    // Data Centers mode is the whole catalog, not an MSA/city/zip
    // layer. Honor the picker as a UI preference but skip the
    // reload — switching back to another metric will reload cleanly.
    if (this.currentMetric === "data_centers") {
      this.currentGeoType = this.autoMode
        ? this.currentGeoType
        : ({ msas: "msa", cities: "city", zips: "zip" }[this.currentViewType] || "msa")
      return
    }

    if (this.autoMode) {
      // Figure out what the current zoom calls for and (if changed) refetch.
      this._resolveAutoLayer()
      return
    }

    this.currentGeoType = { msas: "msa", cities: "city", zips: "zip" }[this.currentViewType] || "msa"

    const defaultZoom = { msa: 4, city: 7, zip: 11 }
    const targetZoom = defaultZoom[this.currentGeoType]
    if (targetZoom && this.map.getZoom() < targetZoom - 1) {
      this.map.easeTo({ zoom: targetZoom, duration: 500 })
    }

    this._loadMarkets()
  }

  onMetricChange(event) {
    const prev = this.currentMetric
    this.currentMetric = event.target.value

    // Data Centers is a different layer entirely — not just a color
    // swap on existing market circles. Route in/out of the dedicated
    // GridSignal pin layer rather than falling through to the paint
    // helper, which assumes the markets source is active.
    if (this.currentMetric === "data_centers") {
      this._showDataCentersLayer()
    } else if (prev === "data_centers") {
      this._hideDataCentersLayer()
      this._applyMetricPaint()
      this._renderLegend(this.featuresById.size)
    } else {
      this._applyMetricPaint()
      this._renderLegend(this.featuresById.size)
    }
  }

  onPeriodChange(event) {
    this.currentPeriod = event.target.value
    // Period only affects the market stats layer; data-center pins
    // are the whole catalog, no time window.
    if (this.currentMetric !== "data_centers") this._loadMarkets()
  }

  // ---------- Data Centers layer (GridSignal) ----------

  async _showDataCentersLayer() {
    // Hide the market circle layers so the data-center pins read
    // against the base tiles instead of layered over them.
    this._setMarketLayerVisibility("none")

    if (!this.map.getSource("gs-projects")) {
      let resp
      try {
        resp = await fetch("/api/gridsignal/map/projects", {
          headers: { "Accept": "application/json" },
          credentials: "same-origin"
        })
      } catch (e) {
        console.error("Data Centers fetch threw:", e)
        this._showToast(`Data Centers: fetch failed (${e.message || e.name}). Check network.`)
        this._renderDataCentersLegend(0)
        return
      }
      if (!resp.ok) {
        // Try to extract the server-reported error from the body —
        // the controller rescues to JSON { error: "..." } so we can
        // surface the real failure instead of the generic status.
        let serverMsg = ""
        try {
          const body = await resp.json()
          if (body && body.error) serverMsg = ` · ${body.error}`
        } catch (_e) { /* body wasn't JSON */ }
        console.error("Data Centers fetch non-OK:", resp.status, resp.statusText, serverMsg)
        this._showToast(`Data Centers: endpoint returned HTTP ${resp.status}${serverMsg}`)
        this._renderDataCentersLegend(0)
        return
      }
      let geojson
      try {
        geojson = await resp.json()
      } catch (e) {
        console.error("Data Centers JSON parse failed:", e)
        this._showToast(`Data Centers: response wasn't JSON — probably an auth redirect to a login page.`)
        this._renderDataCentersLegend(0)
        return
      }
      const count = (geojson.features || []).length
      if (count === 0) {
        // Only toast on failure modes — the legend reports the count
        // on the happy path, so a success toast is noise.
        this._showToast(`Data Centers: endpoint returned 0 projects. Either the catalog is empty or no jurisdictions have coords.`)
      }
      this._dcAll = geojson
      this._dcHidden = this._dcHidden || new Set()
      this._dcNewOnly = this._dcNewOnly || false
      this._addDataCentersLayer(this._dcFiltered())
    } else {
      this._setDataCentersLayerVisibility("visible")
    }
    this._renderDataCentersLegend(this._dcFeatureCount || 0)
  }

  // ---------- Data Centers filters ----------

  _dcFiltered() {
    const features = (this._dcAll?.features || []).filter(f => {
      if (this._dcHidden.has(dcGroupFor(f.properties.stage))) return false
      if (this._dcNewOnly && !f.properties.is_new) return false
      return true
    })
    this._dcFeatureCount = features.length
    return { type: "FeatureCollection", features }
  }

  _applyDcFilter() {
    const source = this.map.getSource("gs-projects")
    if (source) source.setData(this._dcFiltered())
    this._renderDataCentersLegend(this._dcFeatureCount)
  }

  toggleDcStage(event) {
    const key = event.currentTarget.dataset.group
    if (this._dcHidden.has(key)) this._dcHidden.delete(key)
    else this._dcHidden.add(key)
    this._applyDcFilter()
  }

  toggleDcNewOnly() {
    this._dcNewOnly = !this._dcNewOnly
    this._applyDcFilter()
  }

  showDcEarlyStage() {
    this._dcHidden = new Set(DC_LEGEND_GROUPS.map(g => g.key).filter(k => !DC_EARLY_STAGE_GROUPS.includes(k)))
    this._applyDcFilter()
  }

  showAllDc() {
    this._dcHidden = new Set()
    this._dcNewOnly = false
    this._applyDcFilter()
  }

  _hideDataCentersLayer() {
    this._setDataCentersLayerVisibility("none")
    this._setMarketLayerVisibility("visible")
  }

  _addDataCentersLayer(geojson) {
    this.map.addSource("gs-projects", {
      type: "geojson",
      data: geojson,
      cluster: true,
      clusterRadius: 48,
      clusterMaxZoom: 10
    })

    // Cluster bubble — one circle per group of nearby pins with the
    // group count stamped in the center. Users zoom in to break it up.
    this.map.addLayer({
      id: "gs-projects-clusters",
      type: "circle",
      source: "gs-projects",
      filter: ["has", "point_count"],
      paint: {
        "circle-color": "#1E40AF",
        "circle-opacity": 0.85,
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 2,
        "circle-radius": [
          "step", ["get", "point_count"],
          16,  10,
          22,  50,
          30
        ]
      }
    })
    this.map.addLayer({
      id: "gs-projects-cluster-count",
      type: "symbol",
      source: "gs-projects",
      filter: ["has", "point_count"],
      layout: {
        "text-field": ["get", "point_count_abbreviated"],
        "text-size": 13,
        "text-font": ["Noto Sans Regular"],
        "text-allow-overlap": true
      },
      paint: {
        "text-color": "#ffffff"
      }
    })

    // Individual pins — color by stage, size by sqrt(MW).
    // to-number coerces string-valued properties (Rails may emit
    // BigDecimal columns as JSON strings), without which the sqrt
    // expression silently fails and no pins paint.
    this.map.addLayer({
      id: "gs-projects-points",
      type: "circle",
      source: "gs-projects",
      filter: ["!", ["has", "point_count"]],
      paint: {
        "circle-color": ["coalesce", ["get", "stage_color"], "#94A3B8"],
        "circle-opacity": 0.9,
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 1.5,
        // 7px baseline; +0.25px per sqrt(MW); capped at ~18px so a
        // 1200MW Prometheus doesn't dwarf a 20MW colo.
        "circle-radius": [
          "min",
          18,
          ["+", 7, ["*", 0.25, ["sqrt", ["to-number", ["coalesce", ["get", "power_mw"], 0]]]]]
        ]
      }
    })

    this._wireDataCenterInteractions()
  }

  _wireDataCenterInteractions() {
    this.map.on("mouseenter", "gs-projects-points", () => { this.map.getCanvas().style.cursor = "pointer" })
    this.map.on("mouseleave", "gs-projects-points", () => { this.map.getCanvas().style.cursor = "" })
    this.map.on("mouseenter", "gs-projects-clusters", () => { this.map.getCanvas().style.cursor = "pointer" })
    this.map.on("mouseleave", "gs-projects-clusters", () => { this.map.getCanvas().style.cursor = "" })

    this.map.on("click", "gs-projects-points", (e) => {
      const feature = e.features?.[0]
      if (feature) this._openDataCenterSlider(feature)
    })

    // Cluster click → zoom in to expand it.
    this.map.on("click", "gs-projects-clusters", (e) => {
      const feature = e.features?.[0]
      if (!feature) return
      const clusterId = feature.properties.cluster_id
      this.map.getSource("gs-projects").getClusterExpansionZoom(clusterId, (err, zoom) => {
        if (err) return
        this.map.easeTo({ center: feature.geometry.coordinates, zoom })
      })
    })

    // Click anywhere on the map that ISN'T a project pin → close
    // the slider. queryRenderedFeatures scoped to the DC layer so
    // Market Finder's own circles don't accidentally keep the DC
    // slider open.
    this.map.on("click", (e) => {
      const hits = this.map.queryRenderedFeatures(e.point, {
        layers: ["gs-projects-points", "gs-projects-clusters"]
      })
      if (hits.length === 0) this._closeDataCenterSlider()
    })

    // Escape key closes too — standard sidebar/panel convention.
    if (!this._dcEscapeHandler) {
      this._dcEscapeHandler = (e) => {
        if (e.key === "Escape") this._closeDataCenterSlider()
      }
      document.addEventListener("keydown", this._dcEscapeHandler)
    }
  }

  closeDataCenterSlider() { this._closeDataCenterSlider() }

  _closeDataCenterSlider() {
    if (!this.hasDcSliderTarget) return
    this.dcSliderTarget.classList.remove("gs-dc-slider--open")
    this.dcSliderTarget.setAttribute("aria-hidden", "true")
    // Legend is center-bottom with z-index: 500 so it floats over
    // the slider's bottom edge. When the slider is open, dim and
    // offset it to the left so it stays visible but doesn't cover
    // the project details.
    if (this.hasLegendTarget) {
      this.legendTarget.classList.remove("map-legend--slider-open")
    }
  }

  _openDataCenterSlider(feature) {
    if (!this.hasDcSliderTarget || !this.hasDcSliderBodyTarget) {
      // Fall back to the inline popup if the slider DOM isn't on
      // the page (e.g. a view that hasn't been upgraded yet).
      this._openDataCenterPopup(feature)
      return
    }
    const p = feature.properties
    const stageLabel = (p.stage || "UNKNOWN").toString().replace(/_/g, " ")
    const mw = (p.power_mw != null && p.power_mw !== "")
      ? `${Number(p.power_mw).toLocaleString()} MW` : "Capacity unknown"
    const acreage = (p.acreage != null && p.acreage !== "")
      ? `${Number(p.acreage).toLocaleString()} acres` : null
    const developer = p.developer && p.developer !== "null" ? this._escape(p.developer) : "Developer unknown"
    const market = this._escape(p.jurisdiction_label || "")
    const summary = p.summary && p.summary !== "null" ? this._escape(p.summary) : ""
    const address = p.address && p.address !== "null" ? this._escape(p.address) : ""
    const approximate = p.location_source === "jurisdiction"

    // "Scan this area" posts straight to GridSignal's scan creation
    // endpoint for the project's jurisdiction. Reuses the same path
    // the dashboard uses — the ScansController un-hides the market
    // (if the user had removed it) and redirects to the scan
    // progress page. CSRF token read from the Rails meta tag.
    const scanCity  = this._escape(p.jurisdiction_name || "")
    const scanState = this._escape(p.jurisdiction_state || "")
    const scanType  = this._escape(p.jurisdiction_type || "city")
    const csrf      = this.csrfToken || ""

    const html = `
      <div class="gs-dc-popup">
        <div class="gs-dc-popup-head">
          <span class="gs-dc-popup-stage" style="background:${p.stage_color || DC_STAGE_COLORS.UNKNOWN}">${this._escape(stageLabel)}</span>
          <h3 class="gs-dc-popup-name">${this._escape(p.name || "Unnamed project")}</h3>
          <div class="gs-dc-popup-market">${market}</div>
        </div>

        <div class="gs-dc-popup-stats">
          <div class="gs-dc-popup-stat">
            <span class="gs-dc-popup-stat-label">Developer</span>
            <span class="gs-dc-popup-stat-value">${developer}</span>
          </div>
          <div class="gs-dc-popup-stat">
            <span class="gs-dc-popup-stat-label">Capacity</span>
            <span class="gs-dc-popup-stat-value">${mw}</span>
          </div>
          ${acreage ? `
          <div class="gs-dc-popup-stat">
            <span class="gs-dc-popup-stat-label">Site size</span>
            <span class="gs-dc-popup-stat-value">${acreage}</span>
          </div>` : ""}
          ${address ? `
          <div class="gs-dc-popup-stat">
            <span class="gs-dc-popup-stat-label">Address</span>
            <span class="gs-dc-popup-stat-value">${address}</span>
          </div>` : ""}
        </div>

        ${summary ? `<p class="gs-dc-popup-summary">${summary}</p>` : ""}
        ${approximate ? `<p class="gs-dc-popup-caveat">Approximate location — pin is near the market centroid until the address is confirmed.</p>` : ""}

        <div class="gs-dc-popup-actions">
          <form class="gs-dc-popup-form" action="/gridsignal/scans" method="post">
            <input type="hidden" name="authenticity_token" value="${csrf}">
            <input type="hidden" name="city" value="${scanCity}">
            <input type="hidden" name="state" value="${scanState}">
            <input type="hidden" name="jurisdiction_type" value="${scanType}">
            <button type="submit" class="gs-dc-popup-scan-btn" title="Pull this market's municipal records and surface new data-center signals">
              Scan this area for signals
            </button>
          </form>
          <a href="${p.jurisdiction_path}" class="gs-dc-popup-market-btn">Open ${market}</a>
        </div>
      </div>
    `

    // Inject HTML into the slider body and reveal the panel. The
    // slider's own CSS handles the slide-in transform; the body
    // owns the overflow:auto so long summaries scroll instead of
    // clipping (the whole problem with the inline popup).
    this.dcSliderBodyTarget.innerHTML = html
    this.dcSliderTarget.classList.add("gs-dc-slider--open")
    this.dcSliderTarget.setAttribute("aria-hidden", "false")
    this.dcSliderBodyTarget.scrollTop = 0
    // Push the center-bottom legend away from the slider's edge so
    // it stops covering the bottom of the popup. Pure CSS class
    // toggle, reverted on close.
    if (this.hasLegendTarget) {
      this.legendTarget.classList.add("map-legend--slider-open")
    }
  }

  // Legacy popup path, kept as a fallback for views that haven't
  // been upgraded with the slider DOM yet. Same HTML as the slider
  // body, rendered inside a MapLibre popup.
  _openDataCenterPopup(feature) {
    const p = feature.properties
    const [lng, lat] = feature.geometry.coordinates
    const stageLabel = (p.stage || "UNKNOWN").toString().replace(/_/g, " ")
    const mw = (p.power_mw != null && p.power_mw !== "")
      ? `${Number(p.power_mw).toLocaleString()} MW` : "Capacity unknown"
    const acreage = (p.acreage != null && p.acreage !== "")
      ? `${Number(p.acreage).toLocaleString()} acres` : null
    const developer = p.developer && p.developer !== "null" ? this._escape(p.developer) : "Developer unknown"
    const market = this._escape(p.jurisdiction_label || "")
    const summary = p.summary && p.summary !== "null" ? this._escape(p.summary) : ""
    const address = p.address && p.address !== "null" ? this._escape(p.address) : ""
    const approximate = p.location_source === "jurisdiction"
    const scanCity  = this._escape(p.jurisdiction_name || "")
    const scanState = this._escape(p.jurisdiction_state || "")
    const scanType  = this._escape(p.jurisdiction_type || "city")
    const csrf      = this.csrfToken || ""

    const html = `
      <div class="gs-dc-popup">
        <div class="gs-dc-popup-head">
          <span class="gs-dc-popup-stage" style="background:${p.stage_color || DC_STAGE_COLORS.UNKNOWN}">${this._escape(stageLabel)}</span>
          <h3 class="gs-dc-popup-name">${this._escape(p.name || "Unnamed project")}</h3>
          <div class="gs-dc-popup-market">${market}</div>
        </div>
        <div class="gs-dc-popup-stats">
          <div class="gs-dc-popup-stat"><span class="gs-dc-popup-stat-label">Developer</span><span class="gs-dc-popup-stat-value">${developer}</span></div>
          <div class="gs-dc-popup-stat"><span class="gs-dc-popup-stat-label">Capacity</span><span class="gs-dc-popup-stat-value">${mw}</span></div>
          ${acreage ? `<div class="gs-dc-popup-stat"><span class="gs-dc-popup-stat-label">Site size</span><span class="gs-dc-popup-stat-value">${acreage}</span></div>` : ""}
          ${address ? `<div class="gs-dc-popup-stat"><span class="gs-dc-popup-stat-label">Address</span><span class="gs-dc-popup-stat-value">${address}</span></div>` : ""}
        </div>
        ${summary ? `<p class="gs-dc-popup-summary">${summary}</p>` : ""}
        ${approximate ? `<p class="gs-dc-popup-caveat">Approximate location — pin is near the market centroid until the address is confirmed.</p>` : ""}
        <div class="gs-dc-popup-actions">
          <form class="gs-dc-popup-form" action="/gridsignal/scans" method="post">
            <input type="hidden" name="authenticity_token" value="${csrf}">
            <input type="hidden" name="city" value="${scanCity}">
            <input type="hidden" name="state" value="${scanState}">
            <input type="hidden" name="jurisdiction_type" value="${scanType}">
            <button type="submit" class="gs-dc-popup-scan-btn">Scan this area for signals</button>
          </form>
          <a href="${p.jurisdiction_path}" class="gs-dc-popup-market-btn">Open ${market}</a>
        </div>
      </div>
    `
    new maplibregl.Popup({ closeOnClick: true, closeButton: true, anchor: "auto", offset: 14, maxWidth: "420px", className: "gs-dc-popup-wrapper" })
      .setLngLat([lng, lat])
      .setHTML(html)
      .addTo(this.map)
  }

  _setMarketLayerVisibility(value) {
    ["markets-fill", "markets-halo"].forEach(id => {
      if (this.map.getLayer(id)) this.map.setLayoutProperty(id, "visibility", value)
    })
  }

  _setDataCentersLayerVisibility(value) {
    ["gs-projects-points", "gs-projects-clusters", "gs-projects-cluster-count"].forEach(id => {
      if (this.map.getLayer(id)) this.map.setLayoutProperty(id, "visibility", value)
    })
  }

  _renderDataCentersLegend(count) {
    if (!this.hasLegendTarget) return
    const all = this._dcAll?.features || []
    const hidden = this._dcHidden || new Set()
    const newOnly = !!this._dcNewOnly

    // Per-stage counts respect the "New" toggle so the numbers match
    // what tapping a chip would show.
    const counts = {}
    let newCount = 0
    all.forEach(f => {
      if (f.properties.is_new) newCount++
      if (newOnly && !f.properties.is_new) return
      const k = dcGroupFor(f.properties.stage)
      counts[k] = (counts[k] || 0) + 1
    })

    const chips = DC_LEGEND_GROUPS.map(g => {
      const off = hidden.has(g.key)
      return `<button type="button" class="map-legend-tier dc-chip${off ? " dc-chip--off" : ""}"
        style="background:${g.color}" data-group="${g.key}" aria-pressed="${!off}"
        data-action="click->market-map#toggleDcStage"
        title="${off ? "Show" : "Hide"} ${g.label}">${g.label} <span class="dc-chip-count">${counts[g.key] || 0}</span></button>`
    }).join("")

    const filtered = hidden.size > 0 || newOnly
    const total = all.length
    const heading = filtered
      ? `Data Centers · showing ${count} of ${total}`
      : `Data Centers · ${count} project${count === 1 ? "" : "s"}`
    const earlyActive = !newOnly && DC_LEGEND_GROUPS.every(g => hidden.has(g.key) !== DC_EARLY_STAGE_GROUPS.includes(g.key))

    this.legendTarget.innerHTML = `
      <div class="map-legend-label">${heading}</div>
      <div class="map-legend-tiers dc-chips">${chips}</div>
      <div class="dc-quick">
        <button type="button" class="dc-quick-btn${newOnly ? " dc-quick-btn--on" : ""}" aria-pressed="${newOnly}"
          data-action="click->market-map#toggleDcNewOnly" title="Projects first found in the last 90 days">New in last 90 days · ${newCount}</button>
        <button type="button" class="dc-quick-btn${earlyActive ? " dc-quick-btn--on" : ""}"
          data-action="click->market-map#showDcEarlyStage" title="Permitting, site control, and discussion: projects that aren't built yet">Early stage only</button>
        ${filtered ? `<button type="button" class="dc-quick-btn dc-quick-btn--reset" data-action="click->market-map#showAllDc">Show all</button>` : ""}
      </div>
      <div class="dc-hint">Tap a stage to hide or show it</div>
    `
    this.legendTarget.style.display = ""
  }

  // ---------- Search ----------

  onSearchInput() {
    clearTimeout(this.searchTimeout)
    const query = this.searchInputTarget.value.trim()
    this.highlightedResultIndex = -1

    if (query.length < 2) {
      this._hideResults()
      return
    }

    this.searchTimeout = setTimeout(() => this._performSearch(query), 250)
  }

  onSearchKeydown(event) {
    const items = this.searchResultsTarget.querySelectorAll(".search-result-item[data-geo-id]")
    if (event.key === "Escape") { this._hideResults(); return }
    if (event.key === "Enter") {
      event.preventDefault()
      if (this.highlightedResultIndex >= 0 && items[this.highlightedResultIndex]) {
        items[this.highlightedResultIndex].click()
      } else if (items[0]) {
        items[0].click()
      }
      return
    }
    if (event.key === "ArrowDown") {
      event.preventDefault()
      if (!items.length) return
      this.highlightedResultIndex = Math.min(this.highlightedResultIndex + 1, items.length - 1)
      this._updateHighlight(items)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      if (!items.length) return
      this.highlightedResultIndex = Math.max(this.highlightedResultIndex - 1, 0)
      this._updateHighlight(items)
    }
  }

  onSearchSubmit() {
    const first = this.searchResultsTarget.querySelector(".search-result-item[data-geo-id]")
    if (first) first.click()
  }

  async _performSearch(query) {
    try {
      const response = await fetch(`/api/geographies/search?q=${encodeURIComponent(query)}`, {
        headers: {
          "Accept": "application/json",
          ...(this.csrfToken ? { "X-CSRF-Token": this.csrfToken } : {})
        },
        credentials: "same-origin"
      })
      if (!response.ok) return
      const results = await response.json()
      this._renderResults(results)
    } catch (error) {
      console.error("Geo search error:", error)
    }
  }

  _renderResults(results) {
    if (!results.length) {
      this.searchResultsTarget.innerHTML = `<div class="search-result-item no-results">No markets match that search.</div>`
      this.searchResultsTarget.style.display = "block"
      return
    }

    const groups = {}
    const order = []
    results.forEach(r => {
      if (!groups[r.geo_type]) { groups[r.geo_type] = []; order.push(r.geo_type) }
      groups[r.geo_type].push(r)
    })

    let html = ""
    order.forEach(type => {
      const label = groups[type][0].geo_type_label || type.toUpperCase()
      html += `<div class="search-group-header">${label}</div>`
      groups[type].forEach(r => {
        html += `
          <div class="search-result-item"
               data-geo-id="${r.id}"
               data-geo-type="${r.geo_type}"
               data-geo-name="${this._escape(r.display_name)}"
               data-lat="${r.latitude ?? ""}"
               data-lng="${r.longitude ?? ""}"
               data-action="click->market-map#selectResult">
            <span class="geo-type-badge geo-type-${r.geo_type}">${r.geo_type_label || r.geo_type.toUpperCase()}</span>
            ${this._escape(r.display_name)}
          </div>
        `
      })
    })

    this.searchResultsTarget.innerHTML = html
    this.searchResultsTarget.style.display = "block"
    this.highlightedResultIndex = -1
  }

  _updateHighlight(items) {
    items.forEach((item, i) => item.classList.toggle("highlighted", i === this.highlightedResultIndex))
    if (items[this.highlightedResultIndex]) {
      items[this.highlightedResultIndex].scrollIntoView({ block: "nearest" })
    }
  }

  _hideResults() {
    this.searchResultsTarget.style.display = "none"
    this.highlightedResultIndex = -1
  }

  _hideResultsOnOutsideClick(event) {
    if (!this.element.contains(event.target)) this._hideResults()
  }

  // ---------- Selecting a result ----------

  selectResult(event) {
    const item = event.currentTarget
    const id = parseInt(item.dataset.geoId, 10)
    const name = item.dataset.geoName
    const geoType = item.dataset.geoType
    let lat = parseFloat(item.dataset.lat)
    let lng = parseFloat(item.dataset.lng)

    // Switch the Show dropdown + layer to match the picked geography's type —
    // picking an MSA shows the MSA layer, a city shows cities, a ZIP shows ZIPs.
    const typeToView = { msa: "msas", city: "cities", zip: "zips" }
    const nextView = typeToView[geoType]
    const geoTypeChanged = nextView && this.currentGeoType !== geoType

    if (nextView && this.hasViewTypeTarget) {
      this.viewTypeTarget.value = nextView
      this.currentViewType = nextView
      this.autoMode = false
      this.currentGeoType = geoType
    }

    // If the geography record itself has no coords stored, look it up in the
    // already-loaded market features. The MSA layer carries coordinates for
    // every MSA we have data for, so a Denver MSA search will still fly even
    // though its Geography.latitude was never backfilled.
    if ((isNaN(lat) || isNaN(lng) || lat === 0 || lng === 0) && geoType === "msa") {
      const feature = this.featuresById.get(id)
      if (feature) {
        lng = feature.geometry.coordinates[0]
        lat = feature.geometry.coordinates[1]
      }
    }

    this.searchInputTarget.value = name
    this._hideResults()
    this._renderSelectedChip(name)

    this.geographyIdValue = id
    this.geographyNameValue = name

    if (!isNaN(lat) && !isNaN(lng) && lat !== 0 && lng !== 0) {
      this.map.flyTo({
        center: [lng, lat],
        zoom: ZOOM_BY_GEO_TYPE[geoType] || 9,
        speed: 1.4,
        curve: 1.2,
        essential: true
      })

      // Fetch the matching layer so the user sees data at that level
      // immediately after the fly-to completes.
      if (geoTypeChanged) this._loadMarkets()

      if (geoType === "msa") {
        const feature = this.featuresById.get(id)
        if (feature) setTimeout(() => this._openMarketPopup(feature), 600)
      }
    } else {
      this._showToast(`No coordinates saved for ${name}. The market won't be centered — try a bigger geography (MSA) or add coordinates in the admin.`)
    }
  }

  clearGeography() {
    this.geographyIdValue = 0
    this.geographyNameValue = ""
    this.searchInputTarget.value = ""
    this.parentGeoTarget.innerHTML = ""
    this.parentGeoTarget.style.display = "none"

    // Clearing the pinned geography returns the map to Automatic mode so
    // zooming back out starts browsing MSAs again.
    if (this.hasViewTypeTarget) {
      this.viewTypeTarget.value = "auto"
      this.currentViewType = "auto"
      this.autoMode = true
    }

    this.map.flyTo({ center: [-98.5795, 39.8283], zoom: 4, speed: 1.4, essential: true })
    this._resolveAutoLayer()
  }

  _renderSelectedChip(name) {
    this.parentGeoTarget.style.display = ""
    this.parentGeoTarget.innerHTML = `
      <span class="geo-chip" style="border-color: #6ABF4B">
        <span class="geo-chip-dot" style="background: #6ABF4B"></span>
        <span class="geo-chip-label">${this._escape(name)}</span>
        <button class="geo-chip-remove" data-action="click->market-map#clearGeography" title="Remove">&times;</button>
      </span>
    `
  }

  // ---------- Utility ----------

  _showToast(msg) {
    // Lightweight ephemeral toast — no dependency, no stacking logic needed
    // for this app's volume.
    const existing = document.querySelector(".map-toast")
    if (existing) existing.remove()
    const toast = document.createElement("div")
    toast.className = "map-toast"
    toast.textContent = msg
    this.element.appendChild(toast)
    setTimeout(() => toast.classList.add("map-toast--visible"), 20)
    setTimeout(() => {
      toast.classList.remove("map-toast--visible")
      setTimeout(() => toast.remove(), 300)
    }, 4000)
  }

  _escape(str) {
    const div = document.createElement("div")
    div.textContent = str ?? ""
    return div.innerHTML
  }
}
